import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Send, CheckCircle2, Loader2, ArrowLeft, Upload, FileText, X 
} from 'lucide-react';
import { db } from '../Firebase';
import { collection, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import Swal from 'sweetalert2';

export default function ApplyJob() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);

  // Retrieve job position from navigation state, defaulting to empty initially
  const initialPosition = location.state?.position || '';

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    position: initialPosition,
    experience: '',
    message: ''
  });

  useEffect(() => {
    const jobsRef = collection(db, 'jobs');
    const unsubscribe = onSnapshot(jobsRef, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const unique = [];
      const seen = new Set();
      for (const j of list) {
        if (j.title) {
          const key = `${j.title.trim().toLowerCase()}_${(j.location || '').trim().toLowerCase()}_${(j.type || '').trim().toLowerCase()}`;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(j);
          }
        }
      }
      setJobs(unique);
      setJobsLoading(false);

      const initial = location.state?.position;
      const isValid = unique.some(j => j.title === initial) || initial === 'Other / General Application';
      setFormData(prev => ({
        ...prev,
        position: isValid ? initial : (unique[0]?.title || 'Other / General Application')
      }));
    }, (err) => {
      console.error("Error loading jobs in ApplyJob:", err);
      setJobsLoading(false);
    });

    return () => unsubscribe();
  }, [location.state]);

  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, phone: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size limit: 1 MB = 1,048,576 bytes
    if (file.size > 1048576) {
      Swal.fire({
        title: 'File Too Large',
        text: 'File size exceeds the 1 MB limit. Please compress your resume or upload a smaller file.',
        icon: 'warning',
        confirmButtonColor: '#DE2929'
      });
      e.target.value = null; // Reset file input
      setResumeFile(null);
      return;
    }

    // Read file and convert to Base64 to store in Firestore
    const reader = new FileReader();
    reader.onload = () => {
      setResumeFile({
        name: file.name,
        type: file.type,
        sizeKB: Math.round(file.size / 1024),
        base64: reader.result,
        rawFile: file // Keep the raw File object for FormSubmit attachment
      });
    };
    reader.onerror = (err) => {
      console.error('File reading error:', err);
      Swal.fire({
        title: 'Error Processing File',
        text: 'Failed to process file. Please try again.',
        icon: 'error',
        confirmButtonColor: '#DE2929'
      });
    };
    reader.readAsDataURL(file);
  };

  const removeResume = () => {
    setResumeFile(null);
    const fileInput = document.getElementById('resume-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.experience) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Please fill in all required fields (*).',
        icon: 'warning',
        confirmButtonColor: '#DE2929'
      });
      return;
    }

    // Phone validation: exactly 10 digits
    if (formData.phone.length !== 10) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Phone number must be exactly 10 digits.',
        icon: 'warning',
        confirmButtonColor: '#DE2929'
      });
      return;
    }

    // Email validation: if provided, must be valid
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        Swal.fire({
          title: 'Invalid Email Address',
          text: 'Please enter a valid email address.',
          icon: 'warning',
          confirmButtonColor: '#DE2929'
        });
        return;
      }
    }

    if (!resumeFile) {
      Swal.fire({
        title: 'Resume Required',
        text: 'Please upload your resume to submit the application.',
        icon: 'warning',
        confirmButtonColor: '#DE2929'
      });
      return;
    }
    setLoading(true);

    try {
      await addDoc(collection(db, 'job_applications'), {
        ...formData,
        resume: {
          name: resumeFile.name,
          type: resumeFile.type,
          sizeKB: resumeFile.sizeKB,
          base64: resumeFile.base64
        },
        submittedAt: serverTimestamp(),
        status: 'new'
      });

      // Send to FormSubmit
      try {
        const mailData = new FormData();
        mailData.append('_subject', "New Job Application - " + formData.name);
        mailData.append('_template', "table");
        mailData.append('Full Name', formData.name);
        mailData.append('Phone Number', formData.phone);
        mailData.append('Email Address', formData.email || "Not Provided");
        mailData.append('Position Applied For', formData.position);
        mailData.append('Experience', formData.experience);
        mailData.append('Cover Letter / Note', formData.message || "No cover letter");
        if (resumeFile && resumeFile.rawFile) {
          mailData.append('Attachment', resumeFile.rawFile, resumeFile.name);
        }

        await fetch("https://formsubmit.co/ajax/kirandistributors.miraj@gmail.com", {
          method: "POST",
          body: mailData
        });
      } catch (mailErr) {
        console.error("Error sending application to FormSubmit:", mailErr);
      }

      Swal.fire({
        title: 'Application Submitted!',
        text: 'Your application has been submitted successfully.',
        icon: 'success',
        confirmButtonColor: '#DE2929'
      });

      setSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        position: jobs[0]?.title || 'Other / General Application',
        experience: '',
        message: ''
      });
      setResumeFile(null);
    } catch (err) {
      console.error("Error submitting job application: ", err);
      Swal.fire({
        title: 'Submission Failed',
        text: 'Failed to submit application. Please check your network or try again.',
        icon: 'error',
        confirmButtonColor: '#DE2929'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-sans bg-brand-light min-h-screen pt-28 pb-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-8">
        
        {/* Back to Careers Action Bar */}
        <div className="flex items-center justify-between bg-white px-6 py-4 rounded-2xl border border-brand-silver/30 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 font-poppins">
            <span className="hover:text-brand-blue cursor-pointer transition-colors" onClick={() => navigate('/contact?tab=careers')}>Careers</span>
            <span>/</span>
            <span className="text-brand-navy">Apply Online</span>
          </div>
          
          <button 
            onClick={() => navigate('/contact?tab=careers')}
            className="flex items-center gap-1.5 text-xs font-bold font-poppins text-brand-blue hover:text-brand-navy transition-colors"
          >
            <ArrowLeft size={14} /> Back to Job Openings
          </button>
        </div>

        {/* Apply Now Core Form Card */}
        <div className="bg-white p-8 sm:p-12 rounded-3xl border border-brand-silver/50 shadow-md">
          
          <div className="border-l-4 border-brand-blue pl-4 mb-8">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-blue font-poppins block mb-1">Application Form</span>
            <h1 className="text-3xl font-extrabold font-montserrat text-brand-navy">Submit Your Application</h1>
            <p className="text-sm text-gray-500 font-poppins mt-1">Please provide accurate information. Fields marked with (*) are mandatory.</p>
          </div>

          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 space-y-5"
            >
              <div className="w-16 h-16 bg-brand-green/10 text-brand-green rounded-full flex items-center justify-center mx-auto mb-2 animate-bounce">
                <CheckCircle2 size={36} />
              </div>
              <h3 className="text-2xl font-extrabold text-brand-navy font-montserrat">Application Submitted!</h3>
              <p className="text-sm text-gray-500 font-poppins max-w-md mx-auto leading-relaxed">
                Thank you for your interest in joining Kiran Distributors. Your application along with your resume has been securely logged. Our HR desk will contact you soon.
              </p>
              <div className="flex flex-wrap gap-4 justify-center pt-4">
                <button 
                  onClick={() => setSuccess(false)}
                  className="btn-outline py-3 px-6 text-sm border-brand-blue/30 text-brand-blue hover:bg-brand-blue hover:text-white hover:border-brand-blue font-bold font-poppins"
                >
                  Submit Another Application
                </button>
                <Link 
                  to="/contact?tab=careers"
                  className="btn-primary py-3 px-6 text-sm font-bold font-poppins"
                >
                  Back to Careers
                </Link>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Full Name & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-sm text-brand-navy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="92258xxxxx"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-sm text-brand-navy"
                  />
                </div>
              </div>

              {/* Email & Experience */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-sm text-brand-navy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Experience (Years) *</label>
                  <input
                    type="text"
                    name="experience"
                    required
                    placeholder="e.g. 3 Years"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-sm text-brand-navy"
                  />
                </div>
              </div>

              {/* Position Dropdown */}
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Position Applying For</label>
                {jobsLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-3 font-poppins">
                    <Loader2 className="animate-spin text-brand-blue" size={16} />
                    Loading available job openings...
                  </div>
                ) : (
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-sm text-brand-navy"
                  >
                    {jobs.map((job) => (
                      <option key={job.id} value={job.title}>
                        {job.title}
                      </option>
                    ))}
                    <option value="Other / General Application">Other / General Application</option>
                  </select>
                )}
              </div>

              {/* Resume File Upload (1 MB Limit) */}
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Upload Resume (PDF, DOC, DOCX up to 1MB) *</label>
                
                {!resumeFile ? (
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-brand-silver border-dashed rounded-xl hover:border-brand-blue transition-colors duration-200 cursor-pointer relative bg-brand-light/35">
                    <input
                      type="file"
                      id="resume-upload"
                      required
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="space-y-1.5 text-center">
                      <Upload className="mx-auto h-10 w-10 text-gray-400 group-hover:text-brand-blue transition-colors" />
                      <div className="flex text-xs text-gray-600 font-poppins">
                        <span className="relative font-bold text-brand-blue hover:text-brand-navy transition-colors">Upload a file</span>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-[10px] text-gray-500 font-poppins">PDF, DOC, DOCX up to 1 MB</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 rounded-xl border border-brand-blue/20 bg-brand-blue/5 shadow-inner">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand-blue/15 text-brand-blue flex items-center justify-center shrink-0">
                        <FileText size={20} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-brand-navy font-poppins truncate max-w-[200px] sm:max-w-[400px]">
                          {resumeFile.name}
                        </p>
                        <p className="text-[10px] text-gray-500 font-poppins">
                          Size: {resumeFile.sizeKB} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeResume}
                      className="p-1.5 rounded-full hover:bg-gray-200 text-gray-500 hover:text-brand-blue transition-colors"
                      aria-label="Remove resume"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Cover Letter */}
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Cover Letter / Note</label>
                <textarea
                  name="message"
                  rows="5"
                  placeholder="Tell us about yourself, why you'd like to work with us, and your relevant qualifications..."
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-sm resize-none text-brand-navy"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary justify-center font-poppins py-4 text-sm sm:text-base font-bold shadow-lg shadow-brand-blue/10"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Submitting Application...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-1" />
                    Submit Application
                  </>
                )}
              </button>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
